import type { AppendResult } from '../types/navigation';

// Helper functions for navigation
export function getScoreStatusColor(
  score: number,
  total: number,
  statusSlug?: string,
): string {
  if (statusSlug === 'narrative-only') {
    return 'text-meta-grey';
  }

  if (total === 0) {
    return 'text-meta-grey';
  }

  if (statusSlug) {
    switch (statusSlug) {
      case 'meets':
        return 'text-status-green';
      case 'partially-meets':
        return 'text-status-orange';
      case 'does-not-meet':
        return 'text-status-red';
      case 'did-not-review':
      case 'not-eligible':
      case 'does-not-exist':
      case 'no-status':
        return 'text-meta-grey';
    }
  }

  const percentage = (score / total) * 100;
  if (percentage >= 85) return 'text-status-green';
  if (percentage >= 65) return 'text-status-orange';
  return 'text-status-red';
}

export function isMainGatewayMllIndicator(indicator: any): boolean {
  return (
    indicator.slug === 'multilingual-learner-supports' &&
    indicator.rating?.review_status?.slug === 'narrative-only'
  );
}

export function getIndicatorAppend(indicator: any): AppendResult | null {
  if (!indicator.rating) return null;

  const score = indicator.rating.score || 0;
  const total = indicator.rating.total || 0;
  const statusSlug = indicator.rating.review_status?.slug;

  // Skip MLL indicators that are narrative-only
  if (isMainGatewayMllIndicator(indicator)) {
    return null;
  }

  return {
    text: score === 0 && total === 0 ? 'READ' : `${score} / ${total}`,
    class: getScoreStatusColor(score, total, statusSlug),
  };
}

export function getGatewayAppend(gateway: any): AppendResult | null {
  if (!gateway.rating) return null;

  const score = gateway.rating.score || 0;
  const total = gateway.rating.total || 0;
  const statusSlug = gateway.rating.review_status?.slug;

  return {
    text: score === 0 && total === 0 ? 'READ' : `${score} / ${total}`,
    class: getScoreStatusColor(score, total, statusSlug),
  };
}

export function getCriterionAppend(criterion: any): AppendResult | null {
  if (!criterion.rating) return null;

  const score = criterion.rating.score || 0;
  const total = criterion.rating.total || 0;
  const statusSlug = criterion.rating.review_status?.slug;

  return {
    text: score === 0 && total === 0 ? 'READ' : `${score} / ${total}`,
    class: getScoreStatusColor(score, total, statusSlug),
  };
}

export function getCriterionDisplayTitle(
  criterion: any,
  gatewayDetail?: any,
): string {
  if (gatewayDetail?.criterion && Array.isArray(gatewayDetail.criterion)) {
    const detailedCriterion = gatewayDetail.criterion.find(
      (c: any) => c.label === criterion.label,
    );

    if (detailedCriterion) {
      if (
        detailedCriterion.title &&
        detailedCriterion.title !== detailedCriterion.name
      ) {
        return detailedCriterion.title;
      }

      if (detailedCriterion.name && detailedCriterion.label) {
        return `${detailedCriterion.label}: ${detailedCriterion.name}`;
      }

      if (detailedCriterion.label) {
        return detailedCriterion.label;
      }
    }
  }

  if (criterion.title && criterion.title !== criterion.name) {
    return criterion.title;
  }

  if (criterion.name && criterion.label) {
    const repetitiveNames = ['Text Quality', 'Building Knowledge', 'Usability'];
    if (repetitiveNames.includes(criterion.name.trim())) {
      return criterion.label;
    }
    return `${criterion.label}: ${criterion.name}`;
  }

  return criterion.label || 'Criterion';
}
